import axios from 'axios';
import { getEntraApiAccessToken } from '@/services/auth-utils.js';

function api() {
  return axios.create({
    baseURL: process.env.VUE_APP_ENTRA_API_BASE_URL || 'http://localhost:3000',
    timeout: 30000
  });
}

function normaliseError(error) {
  const responseError = error && error.response && error.response.data && error.response.data.error;
  const message = responseError ? responseError.message : (error && error.message || 'An unexpected error occurred.');
  const err = new Error(message);
  err.code = responseError ? responseError.code : (error && error.code ? error.code : 'UNKNOWN_API_ERROR');
  err.correlationId = responseError ? responseError.correlationId : null;
  err.status = error && error.response ? error.response.status : (error && error.status ? error.status : null);
  err.details = responseError ? responseError.details : null;
  return err;
}

async function getHeaders() {
  const token = await getEntraApiAccessToken();
  return {
    Authorization: 'Bearer ' + token,
    'Content-Type': 'application/json'
  };
}

export async function getCurrentRoles() {
  try {
    const token = await getEntraApiAccessToken();
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.roles || [];
  } catch (error) {
    return [];
  }
}

export async function canDeleteOutputs() {
  const roles = await getCurrentRoles();
  return roles.indexOf('Cyanic.Entra.Admin') !== -1;
}

// ==================== Regions ====================

export async function getBillingRegions() {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/regions', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Sync ====================

export async function triggerBillingSync(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/billing/sync', params, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getBillingJobStatus(jobId) {
  if (!jobId) throw new Error('jobId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/jobs/' + encodeURIComponent(jobId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Dashboard ====================

export async function getBillingDashboard(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/dashboard', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Invoices ====================

export async function getBillingInvoices(regionCode, params) {
  if (!regionCode) throw new Error('regionCode is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/regions/' + encodeURIComponent(regionCode) + '/invoices',
      { headers: headers, params: params }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getBillingInvoiceDetail(regionCode, invoiceId) {
  if (!regionCode) throw new Error('regionCode is required');
  if (!invoiceId) throw new Error('invoiceId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/regions/' + encodeURIComponent(regionCode) + '/invoices/' + encodeURIComponent(invoiceId),
      { headers: headers }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getBillingInvoiceLineItems(regionCode, invoiceId, params) {
  if (!regionCode) throw new Error('regionCode is required');
  if (!invoiceId) throw new Error('invoiceId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/regions/' + encodeURIComponent(regionCode) + '/invoices/' + encodeURIComponent(invoiceId) + '/line-items',
      { headers: headers, params: params }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Cost Reports ====================

export async function getBillingCostReports(regionCode, params) {
  if (!regionCode) throw new Error('regionCode is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/regions/' + encodeURIComponent(regionCode) + '/cost-reports',
      { headers: headers, params: params }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getBillingCostDetails(regionCode, invoiceId, params) {
  if (!regionCode) throw new Error('regionCode is required');
  if (!invoiceId) throw new Error('invoiceId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/regions/' + encodeURIComponent(regionCode) + '/cost-reports/' + encodeURIComponent(invoiceId),
      { headers: headers, params: params }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Customers ====================

export async function getBillingCustomers(regionCode, params) {
  if (!regionCode) throw new Error('regionCode is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/regions/' + encodeURIComponent(regionCode) + '/customers',
      { headers: headers, params: params }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getBillingCustomerSummary(regionCode, customerId) {
  if (!regionCode) throw new Error('regionCode is required');
  if (!customerId) throw new Error('customerId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/regions/' + encodeURIComponent(regionCode) + '/customers/' + encodeURIComponent(customerId) + '/summary',
      { headers: headers }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Export ====================

export async function downloadBillingInvoiceCsv(regionCode, invoiceId) {
  if (!regionCode) throw new Error('regionCode is required');
  if (!invoiceId) throw new Error('invoiceId is required');
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/regions/' + encodeURIComponent(regionCode) + '/invoices/' + encodeURIComponent(invoiceId) + '/export',
      { headers: headers, responseType: 'blob', timeout: 120000 }
    );
    return response;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Internal Billing (Options IT) ====================

export async function getInternalBillingDashboard(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/internal/dashboard', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getInternalBillingCustomers(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/internal/customers', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getInternalCustomerSpend(customerId, params) {
  if (!customerId) throw new Error('customerId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/internal/customers/' + encodeURIComponent(customerId) + '/spend',
      { headers: headers, params: params }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getInternalCustomerLineItems(customerId, params) {
  if (!customerId) throw new Error('customerId is required');
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get(
      '/api/v1/billing/internal/customers/' + encodeURIComponent(customerId) + '/line-items',
      { headers: headers, params: params }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getInternalProductSpend(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/internal/product-spend', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Internal Billing Export ====================

export async function downloadInternalBillingCsv(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/internal/export-csv', {
      headers: headers,
      params: params,
      responseType: 'blob',
      timeout: 120000
    });
    return response;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function downloadInternalBillingPdf(params) {
  params = params || {};
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/internal/export-pdf', {
      headers: headers,
      params: params,
      responseType: 'blob',
      timeout: 120000
    });
    return response;
  } catch (error) {
    throw normaliseError(error);
  }
}

// ==================== Recon Processing ====================

export async function processRecon(params) {
  if (!params || !params.reconType || !params.billingMonth) throw new Error('reconType and billingMonth are required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/billing/recon/process', params, { headers: headers, timeout: 300000 });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconStatus() {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/status', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconClientOverview(params) {
  if (!params || !params.reconType || !params.billingMonth) throw new Error('reconType and billingMonth are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/overview/clients', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconAttention(params) {
  if (!params || !params.reconType || !params.billingMonth) throw new Error('reconType and billingMonth are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/attention', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconProductOverview(params) {
  if (!params || !params.reconType || !params.billingMonth) throw new Error('reconType and billingMonth are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/overview/products', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconSpotChecks(params) {
  if (!params || !params.billingMonth) throw new Error('billingMonth is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/spot-checks', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getSkuSuggestions(params) {
  if (!params || !params.billingMonth) throw new Error('billingMonth is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/sku-suggestions', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconAccrualVariance(params) {
  if (!params || !params.month) throw new Error('month is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/accrual-variance', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function listReconOutputs(params) {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/outputs', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function listReconOutputRuns(params) {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/outputs/runs', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function generateReconOutputs(billingMonth) {
  if (!billingMonth) throw new Error('billingMonth is required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/billing/recon/outputs/generate', { billingMonth: billingMonth }, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function downloadReconOutputFile(id) {
  if (!id) throw new Error('id is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/outputs/' + encodeURIComponent(id) + '/download', {
      headers: headers, responseType: 'blob', timeout: 120000
    });
    return response;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function downloadReconOutputBundle(params) {
  if (!params || !params.billingMonth || !params.kind) throw new Error('billingMonth and kind are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/outputs/bundle.zip', {
      headers: headers, params: params, responseType: 'blob', timeout: 300000
    });
    return response;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function deleteReconOutput(id) {
  if (!id) throw new Error('id is required');
  try {
    const headers = await getHeaders();
    await api().delete('/api/v1/billing/recon/outputs/' + encodeURIComponent(id), { headers: headers });
    return true;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function deleteReconOutputRun(runId) {
  if (!runId) throw new Error('runId is required');
  try {
    const headers = await getHeaders();
    const response = await api().delete('/api/v1/billing/recon/outputs/runs/' + encodeURIComponent(runId), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function downloadReconClientWorkbook(params) {
  if (!params || !params.reconType || !params.billingMonth) throw new Error('reconType and billingMonth are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/generate/clients', {
      headers: headers, params: params, responseType: 'blob', timeout: 120000
    });
    return response;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function downloadReconIntacctCsv(params) {
  if (!params || !params.reconType || !params.billingMonth) throw new Error('reconType and billingMonth are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/generate/intacct', {
      headers: headers, params: params, responseType: 'blob', timeout: 120000
    });
    return response;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function downloadReconApCsv(params) {
  if (!params || !params.billingMonth) throw new Error('billingMonth is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/generate/ap', {
      headers: headers, params: params, responseType: 'blob', timeout: 120000
    });
    return response;
  } catch (error) {
    var data = error && error.response && error.response.data;
    if (data instanceof Blob && error.response.status === 422) {
      var parsed = null;
      try {
        parsed = JSON.parse(await data.text());
      } catch (parseError) {
        parsed = null;
      }
      if (parsed && parsed.error === 'INVOICE_VALIDATION_FAILED') {
        throw {
          code: 'INVOICE_VALIDATION_FAILED',
          message: parsed.message || 'Invoice totals do not match gross totals planned for upload.',
          mismatches: parsed.mismatches || [],
          correlationId: parsed.correlationId || null,
          status: 422,
          details: null
        };
      }
    }
    throw normaliseError(error);
  }
}

// ==================== Recon Config ====================

export async function getReconConfig(configKey) {
  if (!configKey) throw new Error('configKey is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/admin/billing/config/' + encodeURIComponent(configKey), { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function saveReconConfig(configKey, data) {
  if (!configKey) throw new Error('configKey is required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/admin/billing/config/' + encodeURIComponent(configKey), data, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function deleteReconConfig(configKey, id) {
  if (!configKey || !id) throw new Error('configKey and id are required');
  try {
    const headers = await getHeaders();
    await api().delete('/api/v1/admin/billing/config/' + encodeURIComponent(configKey) + '/' + encodeURIComponent(id), { headers: headers });
    return true;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconDefaults() {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/admin/billing/config/defaults', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function saveReconDefaults(defaults) {
  try {
    const headers = await getHeaders();
    const response = await api().patch('/api/v1/admin/billing/config/defaults', defaults, { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function importNcePrices(csvContent, month) {
  try {
    const headers = await getHeaders();
    const url = '/api/v1/billing/recon/nce-prices/import' + (month ? '?month=' + encodeURIComponent(month) : '');
    const response = await api().post(url, csvContent, {
      headers: { ...headers, 'Content-Type': 'text/csv' },
      timeout: 120000
    });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function importNcePriceSet(month, files) {
  if (!month) throw new Error('month is required');
  if (!files || !files.length) throw new Error('at least one file is required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/billing/recon/nce-prices/import-set', {
      month: month,
      files: files
    }, { headers: headers, timeout: 600000 });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getNcePriceCoverage() {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/nce-prices/coverage', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getNcePricingReadiness(billingMonth) {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/nce-prices/readiness', { headers: headers, params: { billingMonth: billingMonth } });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconZeroChargeLines(params) {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/profitability/zero-charge', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function fetchFxRates(billingMonth) {
  if (!billingMonth) throw new Error('billingMonth is required');
  try {
    const headers = await getHeaders();
    const response = await api().post('/api/v1/billing/recon/fx-rates/fetch',
      { billingMonth: billingMonth },
      { headers: headers, timeout: 30000 }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconApOverview(params) {
  if (!params || !params.billingMonth) throw new Error('billingMonth is required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/overview/ap', {
      headers: headers,
      params: { billingMonth: params.billingMonth }
    });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconProcessed(params) {
  if (!params || !params.reconType || !params.billingMonth) throw new Error('reconType and billingMonth are required');
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/processed', {
      headers: headers,
      params: {
        reconType: params.reconType,
        billingMonth: params.billingMonth,
        page: params.page || 1,
        pageSize: params.pageSize || 50,
        customerName: params.customerName || undefined,
        generateBill: params.generateBill
      }
    });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function updateReconConfig(configKey, id, data) {
  if (!configKey || !id) throw new Error('configKey and id are required');
  try {
    const headers = await getHeaders();
    const response = await api().patch(
      '/api/v1/admin/billing/config/' + encodeURIComponent(configKey) + '/' + encodeURIComponent(String(id)),
      data, { headers: headers }
    );
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconProfitabilityPeriods() {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/profitability/periods', { headers: headers });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconProfitabilitySummary(params) {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/profitability/summary', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconProfitabilityClients(params) {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/profitability/clients', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}

export async function getReconProfitabilityProducts(params) {
  try {
    const headers = await getHeaders();
    const response = await api().get('/api/v1/billing/recon/profitability/products', { headers: headers, params: params });
    return response.data;
  } catch (error) {
    throw normaliseError(error);
  }
}
